import React, { useState, useMemo } from 'react';
import { Building2, Search, Check, Users } from 'lucide-react';

interface PropostaHeaderProps {
  formData: any;
  setFormData: (data: any) => void;
  clientes: any[];
}

export function PropostaHeader({ formData, setFormData, clientes }: PropostaHeaderProps) {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredClientes = useMemo(() => {
    if (!searchTerm.trim()) return clientes;
    const term = searchTerm.toLowerCase();
    return clientes.filter(c =>
      (c.nome || '').toLowerCase().includes(term) ||
      (c.nome_fantasia || '').toLowerCase().includes(term) ||
      (c.documento || '').toLowerCase().includes(term)
    );
  }, [clientes, searchTerm]);

  const selectedCliente = useMemo(() => {
    return clientes.find(c => String(c.id) === String(formData.pessoa_id));
  }, [clientes, formData.pessoa_id]);

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
        <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-red-600" />
          Dados do Cliente e Status
        </h3>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
          <Users className="w-3.5 h-3.5 text-emerald-600" />
          {clientes.length} Clientes Cadastrados
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-sm font-semibold text-slate-700">
              Cliente <span className="text-red-500">*</span>
            </label>
            {clientes.length > 5 && (
              <span className="text-3xs text-slate-400">
                Apenas clientes ativos e cadastrados
              </span>
            )}
          </div>

          <div className="space-y-2">
            {clientes.length > 8 && (
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Filtrar lista por nome ou documento..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-red-500 focus:bg-white"
                />
              </div>
            )}

            <select
              className="w-full border border-slate-300 rounded-lg px-3 py-2.5 bg-white text-slate-700 outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium text-sm"
              value={formData.pessoa_id || ''}
              onChange={e => setFormData({ ...formData, pessoa_id: e.target.value })}
              required
            >
              <option value="">Selecione o Cliente ({filteredClientes.length} disponíveis)...</option>
              {filteredClientes.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.nome} {c.documento ? `(${c.documento})` : ''}
                </option>
              ))}
            </select>
          </div>

          {selectedCliente && (
            <p className="mt-1 text-2xs text-slate-500">
              Cliente selecionado: <span className="font-semibold text-slate-700">{selectedCliente.nome}</span>
              {selectedCliente.documento ? ` • CNPJ/CPF: ${selectedCliente.documento}` : ''}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1.5">Status da Proposta</label>
          <select
            className="w-full border border-slate-300 rounded-lg px-3 py-2.5 bg-white text-slate-700 outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all font-medium text-sm"
            value={formData.status}
            onChange={e => setFormData({ ...formData, status: e.target.value })}
          >
            <option value="Rascunho">Rascunho</option>
            <option value="Enviado">Enviado</option>
            <option value="Aprovado">Aprovado</option>
            <option value="Reprovado">Reprovado</option>
          </select>
        </div>
      </div>
    </div>
  );
}
